import { createRoot } from "react-dom/client"
import { type JSX, useState } from "react"
import { RowMenu } from "../shared/row_menu"
import { ShareDialog } from "./share_dialog"
import { DeleteDialog } from "./delete_dialog"

// The workspace ⋯ menu. One component for both places it appears — a row on
// /user/workspaces and the rail on the workspace page — so the items, the
// dialogs and the keyboard behaviour cannot drift apart between them.
//
// Both entries are owner-only actions, so the menu renders nothing at all when
// the viewer is not the owner rather than offering items that would 404.
export type WorkspaceMenuProps = {
  workspaceName: string
  sharingUrl: string
  deleteUrl: string
  canManage: boolean
}

export function WorkspaceMenu({
  workspaceName,
  sharingUrl,
  deleteUrl,
  canManage,
}: WorkspaceMenuProps): JSX.Element | null {
  const [sharing, setSharing] = useState(false)
  const [deleting, setDeleting] = useState(false)

  if (!canManage) return null

  return (
    <RowMenu
      label={`Actions for ${workspaceName}`}
      items={[
        { id: "share", label: "Sharing", onSelect: () => setSharing(true) },
        { id: "delete", label: "Delete workspace", danger: true, onSelect: () => setDeleting(true) },
      ]}
    >
      <ShareDialog
        open={sharing}
        workspaceName={workspaceName}
        sharingUrl={sharingUrl}
        onClose={() => setSharing(false)}
      />
      <DeleteDialog
        open={deleting}
        workspaceName={workspaceName}
        deleteUrl={deleteUrl}
        onClose={() => setDeleting(false)}
      />
    </RowMenu>
  )
}

// Mounts the menu onto every [data-workspace-menu] on the page — the rows of
// /user/workspaces. The workspace page renders <WorkspaceMenu> directly inside
// its own React tree instead; this is only for the ERB side.
export function mountWorkspaceMenus(): void {
  document.querySelectorAll<HTMLElement>("[data-workspace-menu]").forEach((el) => {
    createRoot(el).render(
      <WorkspaceMenu
        workspaceName={el.dataset.workspaceMenuName ?? ""}
        sharingUrl={el.dataset.workspaceMenuSharingUrl ?? ""}
        deleteUrl={el.dataset.workspaceMenuDeleteUrl ?? ""}
        canManage={el.dataset.workspaceMenuCanManage === "true"}
      />,
    )
  })
}
